import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { getNoticeListApi } from '~/features/notice/api/get-notice-list.api';
import { getNoticeApi } from '~/features/notice/api/get-notice.api';

import type { PaginatedApiResponse } from '~/common/types/api.type';
import type { PaginationMeta } from '~/common/types/pagination.type';
import type { Notice, NoticeListParams } from '~/features/notice/types/notice.type';

export interface NoticeState {
  isLoading: boolean;
  list: Notice[];
  meta: PaginationMeta | null;
  params: NoticeListParams | null;
  selectedNotice: Notice | null;
}

const initialState: NoticeState = {
  isLoading: false,
  list: [],
  meta: null,
  params: null,
  selectedNotice: null,
};

/**
 * 공지사항 목록 조회
 */
export const fetchNoticeList = createAsyncThunk<
  PaginatedApiResponse<Notice>,
  NoticeListParams | undefined
>('notice/fetchList', async (params, { rejectWithValue }) => {
  try {
    const { data } = await getNoticeListApi(params);
    return data;
  } catch (err) {
    return rejectWithValue(err);
  }
});

/**
 * 공지사항 상세 조회
 */
export const fetchNoticeDetail = createAsyncThunk<Notice, number>(
  'notice/fetchDetail',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await getNoticeApi(id);
      return data.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const NoticeSlice = createSlice({
  initialState,
  name: 'notice',
  reducers: {
    setSelectedNotice: (state, action) => {
      state.selectedNotice = action.payload;
    },
    resetSelectedNotice: state => {
      state.selectedNotice = null;
    },
    resetNotice: state => {
      state.selectedNotice = null;
      state.list = [];
      state.meta = null;
      state.params = null;
      state.isLoading = false;
    },
  },
  extraReducers: builder => {
    builder
      // fetchNoticeList
      .addCase(fetchNoticeList.pending, (state, action) => {
        state.isLoading = true;
        state.params = action.meta.arg ?? null;
      })
      .addCase(fetchNoticeList.fulfilled, (state, action) => {
        state.list = Array.isArray(action.payload.data) ? action.payload.data : [];
        state.meta = action.payload.meta ?? null;
        state.isLoading = false;
      })
      .addCase(fetchNoticeList.rejected, state => {
        state.isLoading = false;
      })
      // fetchNoticeDetail
      .addCase(fetchNoticeDetail.pending, state => {
        state.isLoading = true;
      })
      .addCase(fetchNoticeDetail.fulfilled, (state, action) => {
        state.selectedNotice = action.payload;
        state.isLoading = false;
      })
      .addCase(fetchNoticeDetail.rejected, state => {
        state.isLoading = false;
      });
  },
});

export const { setSelectedNotice, resetSelectedNotice, resetNotice } = NoticeSlice.actions;
export default NoticeSlice;
